/* eslint-disable class-methods-use-this */
import type { IndexedParams, RouterLocation } from "@vaadin/router";
import { LitElement } from "lit";
import { apiBaseUrl } from "./constants";
import { fetchApi } from "./functions/fetchApi";
import { shadowFocusHandler } from "./functions/shadow-focus-handler";
import { OrderByOptions } from "./orderby.enum";
import { categoryIcons } from "./svg/categories";
import type { FilterChangedEvent } from "./events";
import type { Category } from "./components";
import type { UmbracoVersion, ScheduledTask, State } from "./entities";

const [versions, categories, scheduledTasks] = await Promise.all([
  fetchApi<Array<UmbracoVersion>>(`${apiBaseUrl()}umbracoversions`),
  fetchApi<Array<Category>>(`${apiBaseUrl()}categories`),
  fetchApi<Array<ScheduledTask>>(`${apiBaseUrl()}tasks`),
]);

let versionOptions = versions!.map((v: UmbracoVersion) => ({
  name: v.version,
  value: v.version,
  selected: false,
}));

// when filtering by a major (eg Version 12) the provided filter value
// must be the latest minor for that major (eg 12.3)

function getLatestMinorForMajor(majorNumber: string) {
  for (let i = versionOptions.length - 1; i >= 0; i -= 1) {
    if (versionOptions[i].value.startsWith(majorNumber)) {
      return versionOptions[i].value;
    }
  }
  return `${majorNumber}.0`;
}

versionOptions = [
  ...versionOptions,
  ...(versionOptions
    .filter((major) => major.value.endsWith(".0"))
    .map((major) => {
      const majorNumber = major.value.split(".").at(0)!;
      const latestMinorForMajor = getLatestMinorForMajor(majorNumber);
      return {
        name: `Version ${majorNumber!}`,
        value: latestMinorForMajor,
        selected: false,
      };
    }) ?? []),
];

versionOptions
  .sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true }))
  .reverse();

versionOptions.splice(0, 0, {
  name: "Any",
  value: "",
  selected: true,
});

const params: any = new Proxy(new URLSearchParams(window.location.search), {
  get: (searchParams, prop: string) => searchParams.get(prop),
});

// full version may be major.minor.patch, we only need major.minor
if (params.umbversion) {
  const majorMinor = params.umbversion.split(".").slice(0, 2).join(".");
  versionOptions.forEach((v) => {
    v.selected = v.value === majorMinor;
  });
}

const _state: State = {
  scheduledTasks: scheduledTasks ?? [],
  filter: {},
  displayData: {},
  query: "",
  queryKey: "",
  orderBy: "",
  isBackoffice: false,
  showClear: false,
  categories: categories!
    .map((c: Category) => ({
      name: c.name,
      link: `/category/${c.name.toLowerCase().replace(/\s|\//g, "-")}`,
      icon: categoryIcons.find(
        (i) => i.key.toLowerCase() === c.id.toLowerCase()
      )?.icon,
      id: c.id,
      tooltip: c.tooltip,
      description: c.description,
      umbracoWebsiteNodeId: c.umbracoWebsiteNodeId,
    }))
    .filter((x: any) => !!x.icon),
  umbracoVersions: versionOptions,
  filters: [
    {
      label: "Umbraco version",
      alias: "supportsUmbracoVersionNumber",
      controlType: "select",
      options: versionOptions,
    },
    {
      label: "Maintained by",
      alias: "maintainedBy",
      controlType: "radio",
      options: [
        { name: "Any", value: "", selected: true },
        { name: "Umbraco HQ", value: "hq" },
        { name: "Community", value: "community" },
      ],
    },
    {
      label: "License type",
      alias: "licenseType",
      controlType: "radio",
      options: [
        { name: "Any", value: "", selected: true },
        { name: "Free", value: "Free" },
        { name: "Purchase", value: "Purchase" },
        { name: "Subscription", value: "Subscription" },
      ],
    },
    {
      label: "Type",
      alias: "packageType",
      controlType: "radio",
      options: [
        { name: "Any", value: "", selected: true },
        { name: "Package", value: "Package" },
        { name: "Integration", value: "Integration" },
        { name: "Template", value: "Template" },
        { name: "Tool", value: "Tool" },
      ],
    },
    {
      label: "Order by",
      alias: "orderBy",
      controlType: "radio",
      tooltip: `<ul>
        <li><strong>Most active installs</strong>: the number of installations reporting using the package within the last 14 days.</li>
        <li><strong>Most downloads</strong>: the all-time number of downloads as reported by NuGet.</li>
        <li><strong>Recently added</strong>: the date the package was first added to the Umbraco Marketplace.</li>
        <li><strong>Recently updated</strong>: the date the package was last updated on the Umbraco Marketplace.</li></ul>`,
      options: [
        {
          name: "Most active installs",
          value: OrderByOptions.Installs,
          selected: true,
        },
        { name: "Most downloads", value: OrderByOptions.Downloads },
        { name: "Relevance", value: OrderByOptions.Relevance },
        { name: "Recently added", value: OrderByOptions.Created },
        { name: "Recently updated", value: OrderByOptions.Modified },
        { name: "A-Z", value: OrderByOptions.AZ },
        { name: "Z-A", value: OrderByOptions.ZA },
      ],
    },
  ],
};

// needs to happen after _state is declared as we need to access _state.filters
const getFiltersFromSearchParams = () => {
  const query = new URLSearchParams(window.location.search);
  const filter: Record<string, any> = {};
  query.forEach((value, key) => {
    // only set filters for valid filter aliases
    const filterObj = _state.filters?.find((x) => x.alias === key);
    if (!filterObj) return;

    // only set valid values - this avoids setting all to false
    // when the query value doesn't match a filter option
    const filterOption = filterObj.options.find((o) => o.value === value);
    if (!filterOption) return;

    // select the matching filter option
    filterObj.options.forEach((o) => {
      o.selected = o.value === value;
    });

    // and finally, update the filters used for the card query
    filter[key] = value;
  });

  return filter;
};

_state.filter = getFiltersFromSearchParams();

export class StateController {
  host: LitElement;

  static lastFocusedElement: HTMLElement;

  static current = _state;

  static get maxUmbracoVersion() {
    if (_state.maxUmbracoVersion) return _state.maxUmbracoVersion;

    _state.maxUmbracoVersion = Math.max(
      ..._state
        .umbracoVersions!.filter((x) => x.value)
        .map((x) => parseInt(x.value, 10))
    );
    return _state.maxUmbracoVersion;
  }

  static orderByKey = "orderBy";

  constructor(host: LitElement) {
    this.host = host;
    this.host.addController(this);
  }

  hostConnected() {}

  hostDisconnected() {}

  setFilter(key: string, value: string, raiseEvent = true) {
    if (_state.filter === undefined) {
      _state.filter = {};
    }

    // also update selected value in filters collection to ensure
    // correct filter is set after route change
    const filterToSet = _state.filters?.find((x) => x.alias === key);
    filterToSet?.options.forEach((x) => {
      x.selected = x.value.toLowerCase() === value.toLowerCase();
    });

    // set filter after finding correctly cased value above
    _state.filter[key] =
      filterToSet?.options.find((x) => x.selected)?.value ?? "";

    if (!raiseEvent) return;

    StateController.syncUrlFilters();

    window.dispatchEvent(
      new CustomEvent<FilterChangedEvent>("filter-changed", {
        detail: { key, value },
      })
    );

    this.host.requestUpdate();
  }

  setQuery(value: string) {
    _state.query = value;

    this.host.requestUpdate();
  }

  setCategories(cats: Array<Category>) {
    _state.categories = cats;

    // this event triggers the card refresh in the list view,
    // it doesn't need to fire on homepage view as the categories
    // are not required for building the query
    if (StateController.isCategoryView) {
      this.host.requestUpdate();
    }
  }

  setPageTitleAndDescription(query: string, urlParams?: IndexedParams) {
    _state.pageTitle = undefined;
    _state.pageDescription = undefined;

    if (!urlParams) {
      return;
    }

    _state.pageTitle = urlParams.pageTitle
      ?.toString()
      .replace(/\{.*}/gi, query);
    _state.pageDescription = urlParams.pageDescription
      ?.toString()
      .replace(/\{.*}/gi, query);

    _state.showClear = urlParams.showClear ? !!+urlParams.showClear : false;
  }

  set(params: IndexedParams, location: RouterLocation) {
    const query = location.params?.id
      ? window.decodeURI(location.params.id.toString())
      : "";

    _state.queryKey = params?.query?.toString() ?? "";
    _state.query = query;

    if (StateController.isTypeView) {
      this.setFilter("packageType", query, false);
    }

    if (_state.queryKey === "hallOfFameOnly") {
      _state.query = "true";
    }

    if (_state.query === "hq" && _state.queryKey === "tags") {
      this.setFilter("maintainedBy", _state.query);
      _state.query = "";
      _state.queryKey = "";
    }

    let orderByDisplay = "";
    if (params.orderBy) {
      const orderByEnumValue = this.#getOrderByFromParams(params);

      if (orderByEnumValue) {
        const orderByFilter = _state.filters?.find(
          (x) => x.alias === StateController.orderByKey
        );

        orderByDisplay =
          orderByFilter!.options.find((x) => x.value === orderByEnumValue)
            ?.name ?? "";

        this.setFilter(StateController.orderByKey, orderByEnumValue, false);
        _state.orderBy = orderByEnumValue.toString();
      }
    }

    const pageTitleDescription =
      location.pathname.startsWith("/all") && orderByDisplay
        ? orderByDisplay
        : query;

    this.setPageTitleAndDescription(pageTitleDescription, params);
  }

  #getOrderByFromParams(queryParams: IndexedParams) {
    const { orderBy } = queryParams;
    const orderByValue = orderBy?.toString().toLowerCase();
    let orderByIndex = Object.keys(OrderByOptions).findIndex(
      (key) => key.toLowerCase() === orderByValue
    );

    if (orderByIndex === -1) {
      orderByIndex = Object.values(OrderByOptions).findIndex(
        (v) => v.toLowerCase() === orderByValue
      );
    }

    if (orderByIndex === -1) {
      return OrderByOptions.Installs;
    }

    return Object.values(OrderByOptions).at(orderByIndex);
  }

  /**
   * Set filters back to default/empty values
   */
  clearFilters() {
    window.history.replaceState(null, "", window.location.pathname);

    if (!_state.filter || Object.keys(_state.filter).length === 0) {
      return;
    }

    _state.filter = {};
    _state.filters?.forEach((filter) => {
      filter.options?.forEach((option, index) => {
        option.selected = index === 0;
      });
    });

    window.dispatchEvent(new CustomEvent<FilterChangedEvent>("filter-cleared"));
  }

  static syncUrlFilters() {
    if (_state.filter === undefined) return;

    const filterKeys = Object.keys(_state.filter);
    const activeFilters = filterKeys.filter((key) => _state.filter![key]);
    const queryParams = new URLSearchParams(window.location.search);

    // when moving away from a search, we don't want to update
    // the querystring with orderBy, and we also don't want the
    // orderBy filter set - this resets both
    if (
      !this.isSearchView &&
      _state.filter[this.orderByKey] === OrderByOptions.Relevance
    ) {
      const idx = filterKeys.indexOf(this.orderByKey);
      if (idx !== -1) {
        filterKeys.splice(idx, 1);
        delete _state.filter[this.orderByKey];
        _state.filters
          ?.find((x) => x.alias === this.orderByKey)
          ?.options.forEach((option, index) => {
            option.selected = index === 0;
          });
      }
    }

    filterKeys.forEach((key) => {
      if (activeFilters.includes(key)) {
        queryParams.set(key, _state.filter![key]);
      } else {
        queryParams.delete(key);
      }
    });

    let queryString = queryParams.toString();
    queryString = queryString.length ? `?${queryString}` : "";

    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${queryString}`
    );
  }

  clear() {
    _state.query = "";
    _state.queryKey = "";
    this.clearFilters();
  }

  get state() {
    return _state;
  }

  static get isPackageView() {
    return this._isRoute("/package");
  }

  static get isSearchView() {
    return this._isRoute("/search");
  }

  static get isCategoryView() {
    return this._isRoute("/category");
  }

  static get isTypeView() {
    return this._isRoute("/type");
  }

  static get isTagsView() {
    return this._isRoute("/tag");
  }

  static get isAllView() {
    return this._isRoute("/all");
  }

  static get isEmbedView() {
    return this._isRoute("/embed");
  }

  private static _isRoute(path: string): boolean {
    return window.location.pathname.startsWith(path);
  }

  static variant: "A" | "B" | undefined;
}

document.addEventListener("focus", shadowFocusHandler, true);
document.addEventListener("-shadow-focus", (e: any) => {
  StateController.lastFocusedElement = e.detail;
});
